// Central registry of code-split route chunks.
//
// Why this file exists rather than inline `lazy(() => import(...))` calls in
// App.tsx: the raw loaders have to be reachable outside the render tree so we
// can *prefetch* a chunk before React asks for it (see prefetchRoute). Keeping
// the loader and the lazy component derived from one object is what stops the
// two from drifting apart.
//
// Splitting is not free — a lazy route costs an extra request on first visit.
// It pays here because the entry chunk was a single 2,560 kB file (770 kB gzip)
// in which `echarts` + `zrender` alone accounted for 1,112 kB, reachable only
// from the Dashboard route. Every user downloaded the whole charting stack to
// render the login form.

import { lazy, type ComponentType } from 'react';

/** Named-export module shape: `{ HomePage: ... }` rather than a default export. */
type Loader = () => Promise<Record<string, unknown>>;

interface RouteChunk {
  load: Loader;
  export: string;
}

const chunk = (load: Loader, exportName: string): RouteChunk => ({ load, export: exportName });

/**
 * Every code-split route, keyed by a stable name used for prefetching.
 *
 * LoginPage is deliberately absent: it is the cold entry point for signed-out
 * users, so splitting it would add a round-trip to the one render that has no
 * cache to fall back on.
 */
export const routeChunks = {
  home: chunk(() => import('./home'), 'HomePage'),
  dashboard: chunk(() => import('./dashboard'), 'DashboardPage'),
  myTasks: chunk(() => import('./my-tasks'), 'MyTasksPage'),
  projects: chunk(() => import('./projects/index'), 'ProjectsListPage'),
  projectNew: chunk(() => import('./projects/new'), 'CreateProjectPage'),
  projectDetail: chunk(() => import('./projects/[id]'), 'ProjectDetailPage'),
  projectForm: chunk(() => import('./projects/[id]/forms/[formId]'), 'ProjectFormPage'),
  taskNew: chunk(() => import('./projects/[id]/tasks/new'), 'CreateTaskPage'),
  taskDetail: chunk(() => import('./projects/[id]/tasks/[taskId]'), 'TaskDetailPage'),
  notifications: chunk(() => import('./notifications'), 'NotificationsPage'),
  team: chunk(() => import('./team'), 'TeamPage'),
  reports: chunk(() => import('./reports/index'), 'ReportsHubPage'),
  settings: chunk(() => import('./settings'), 'SettingsPage'),
  users: chunk(() => import('./users/index'), 'UsersListPage'),
  userInvite: chunk(() => import('./users/invite'), 'InviteUserPage'),
  userDetail: chunk(() => import('./users/[id]'), 'UserDetailPage'),
  formTemplates: chunk(() => import('./form-templates/index'), 'FormTemplatesListPage'),
  formTemplateBuilder: chunk(() => import('./form-templates/[id]'), 'FormTemplateBuilderPage'),
  roles: chunk(() => import('./roles/index'), 'RolesListPage'),
  roleNew: chunk(() => import('./roles/new'), 'CreateRolePage'),
  roleDetail: chunk(() => import('./roles/[id]'), 'RoleDetailPage'),
  onboarding: chunk(() => import('./onboarding/index'), 'OnboardingPage'),
  resetPassword: chunk(() => import('./reset-password'), 'ResetPasswordPage'),
  invite: chunk(() => import('./invite'), 'AcceptInvitePage'),
  notFound: chunk(() => import('./not-found'), 'NotFoundPage'),
} satisfies Record<string, RouteChunk>;

export type RouteName = keyof typeof routeChunks;

function toLazy(name: RouteName): ComponentType<Record<string, never>> {
  const { load, export: exportName } = routeChunks[name];
  return lazy(async () => ({
    default: (await load())[exportName] as ComponentType<Record<string, never>>,
  }));
}

/**
 * Warm a route's chunk without rendering it.
 *
 * Safe to call repeatedly — the module registry dedupes, so a hover that fires
 * twenty times still issues one request. Failures are swallowed: a prefetch is
 * an optimisation, and letting a transient chunk 404 reject here would surface
 * as an unhandled rejection with no user-visible cause. The real navigation
 * retries and shows a proper error.
 *
 * Returns the in-flight load so callers that warm several routes can wait for
 * one to land before starting the next. Firing them all at once saturates the
 * connection pool and delays whatever the *current* route is still fetching.
 */
export function prefetchRoute(name: RouteName): Promise<void> {
  return routeChunks[name]
    .load()
    .then(() => undefined)
    .catch(() => undefined);
}

export const LazyRoutes = {
  Home: toLazy('home'),
  Dashboard: toLazy('dashboard'),
  MyTasks: toLazy('myTasks'),
  Projects: toLazy('projects'),
  ProjectNew: toLazy('projectNew'),
  ProjectDetail: toLazy('projectDetail'),
  ProjectForm: toLazy('projectForm'),
  TaskNew: toLazy('taskNew'),
  TaskDetail: toLazy('taskDetail'),
  Notifications: toLazy('notifications'),
  Team: toLazy('team'),
  Reports: toLazy('reports'),
  Settings: toLazy('settings'),
  Users: toLazy('users'),
  UserInvite: toLazy('userInvite'),
  UserDetail: toLazy('userDetail'),
  FormTemplates: toLazy('formTemplates'),
  FormTemplateBuilder: toLazy('formTemplateBuilder'),
  Roles: toLazy('roles'),
  RoleNew: toLazy('roleNew'),
  RoleDetail: toLazy('roleDetail'),
  Onboarding: toLazy('onboarding'),
  ResetPassword: toLazy('resetPassword'),
  Invite: toLazy('invite'),
  NotFound: toLazy('notFound'),
};
